import React, { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ActiveChannelTypeEnum, TENANT_TYPES } from 'constants-channel';
import { ICON_SET_FONTAWESOME } from 'constants-activate';
import { getDisplayName } from 'lane-shared/helpers';
import { Channel } from 'application-types';

import { H4, P, Button, Flex, Icon, Modal } from 'design-system-web';

import { ConfirmationModal } from '../general/ConfirmationModal';
import { ChannelPickerRow } from './ChannelPickerRow';

import styles from './HomeChannelPickerModal.scss';

function isPropertyChannel(channel: Channel) {
  return channel.type === ActiveChannelTypeEnum.Property;
}

function isTenantChannel(channel: Channel) {
  return TENANT_TYPES.includes(channel.type);
}

type PropertyGroup = {
  property: Channel;
  tenants: Channel[];
};

type OrganisationGroup = {
  // Null when the parent org channel is not in the user's channel list.
  // Users hold property-level membership, so org channels are not always present.
  organisation: Channel | null;
  properties: PropertyGroup[];
};

function buildOrganisationGroups(channels: Channel[]): OrganisationGroup[] {
  const properties = channels.filter(isPropertyChannel);
  const tenants = channels.filter(isTenantChannel);

  function withTenants(property: Channel): PropertyGroup {
    return {
      property,
      tenants: tenants.filter(tenant => tenant.parent?._id === property._id),
    };
  }

  const organisationIds = Array.from(
    new Set(
      properties
        .map(property => property.parent?._id)
        .filter((id): id is string => Boolean(id))
    )
  );

  const groupedByOrg: OrganisationGroup[] = organisationIds.map(
    organisationId => ({
      // org channel may not be in channels if user only has property membership
      organisation:
        channels.find(channel => channel._id === organisationId) ?? null,
      properties: properties
        .filter(property => property.parent?._id === organisationId)
        .map(withTenants),
    })
  );

  const ungroupedProperties = properties.filter(
    property =>
      !property.parent?._id || !organisationIds.includes(property.parent._id)
  );

  if (ungroupedProperties.length > 0) {
    groupedByOrg.push({
      organisation: null,
      properties: ungroupedProperties.map(withTenants),
    });
  }

  return groupedByOrg;
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
  channels: Channel[];
  currentHomeChannelId?: string | null;
  onConfirm: (channel: Channel) => Promise<void>;
};

export function HomeChannelPickerModal({
  isOpen,
  onClose,
  channels,
  currentHomeChannelId,
  onConfirm,
}: Props) {
  const { t } = useTranslation();
  const [selectedChannelId, setSelectedChannelId] = useState<string | null>(
    null
  );
  const [isConfirming, setIsConfirming] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [expandedPropertyIds, setExpandedPropertyIds] = useState<Set<string>>(
    () =>
      new Set(
        channels
          .filter(c => c.type === ActiveChannelTypeEnum.Property)
          .map(c => c._id)
      )
  );

  const organisationGroups = useMemo(
    () => buildOrganisationGroups(channels),
    [channels]
  );
  const selectedChannel = channels.find(
    channel => channel._id === selectedChannelId
  );

  function handleClose() {
    setSelectedChannelId(null);
    setIsConfirming(false);
    setSaveError(null);
    onClose();
  }

  function handleCancelConfirmation() {
    setIsConfirming(false);
    setSaveError(null);
  }

  function toggleProperty(propertyId: string) {
    setExpandedPropertyIds(prev => {
      const next = new Set(prev);

      if (next.has(propertyId)) {
        next.delete(propertyId);
      } else {
        next.add(propertyId);
      }

      return next;
    });
  }

  async function handleConfirm() {
    if (!selectedChannel || isSaving) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      await onConfirm(selectedChannel);
      handleClose();
    } catch {
      setSaveError(t('web.userProfile.homeChannel.saveError'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <>
      <Modal
        className={styles.modal}
        isOpen={isOpen && !isConfirming}
        onClose={handleClose}
        title={t('web.userProfile.homeChannel.setHomeChannel')}
        testId="homeChannelPickerModal"
        includeHeader={false}
        isCloseButtonHidden
        disableContentPadding
      >
        <Flex justify="space-between" align="center" className={styles.header}>
          <Flex align="center" gap={2}>
            <Icon
              className={styles.headerIcon}
              name="home"
              set={ICON_SET_FONTAWESOME}
              type="far"
            />
            <div>
              <H4>{t('web.userProfile.homeChannel.setHomeChannel')}</H4>
              <P className={styles.subtitle}>
                {t('web.userProfile.homeChannel.subtitle')}
              </P>
            </div>
          </Flex>
          <button
            className={styles.closeButton}
            onClick={handleClose}
            type="button"
            data-test="closeButton"
            aria-label={t('Close')}
          >
            <Icon name="times" set={ICON_SET_FONTAWESOME} type="far" />
          </button>
        </Flex>

        <div className={styles.list}>
          {organisationGroups.map((group, index) => (
            <div key={group.organisation?._id || `ungrouped-${index}`}>
              {group.organisation && (
                <P className={styles.groupHeader}>
                  {getDisplayName(group.organisation)}
                </P>
              )}
              {group.properties.map(({ property, tenants }) => {
                const isExpanded = expandedPropertyIds.has(property._id);

                return (
                  <div key={property._id}>
                    <ChannelPickerRow
                      channel={property}
                      isSelected={selectedChannelId === property._id}
                      isHome={currentHomeChannelId === property._id}
                      tenantCount={tenants.length}
                      isExpanded={isExpanded}
                      onSelect={() => setSelectedChannelId(property._id)}
                      onToggleExpand={
                        tenants.length > 0
                          ? () => toggleProperty(property._id)
                          : undefined
                      }
                    />
                    {isExpanded &&
                      tenants.map(tenant => (
                        <ChannelPickerRow
                          key={tenant._id}
                          channel={tenant}
                          isTenantRow
                          isSelected={selectedChannelId === tenant._id}
                          isHome={currentHomeChannelId === tenant._id}
                          onSelect={() => setSelectedChannelId(tenant._id)}
                        />
                      ))}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <Flex gap={3} className={styles.footer}>
          <Button
            variant="primary"
            size="large"
            disabled={!selectedChannelId}
            onClick={() => {
              setSaveError(null);
              setIsConfirming(true);
            }}
            testId="confirmHomeChannelSelection"
          >
            {t('web.userProfile.homeChannel.confirm')}
          </Button>
          <Button variant="secondary" size="large" onClick={handleClose}>
            {t('web.userProfile.homeChannel.cancel')}
          </Button>
        </Flex>
      </Modal>

      <ConfirmationModal
        isOpen={isOpen && isConfirming && Boolean(selectedChannel)}
        onClose={handleCancelConfirmation}
        handleConfirm={handleConfirm}
        title={t('web.userProfile.homeChannel.setAsHomeChannelTitle')}
        description={t('web.userProfile.homeChannel.confirmDescription', {
          channelName: selectedChannel ? getDisplayName(selectedChannel) : '',
        })}
        confirmText={t('web.userProfile.homeChannel.yesSetAsHome')}
        cancelText={t('web.userProfile.homeChannel.cancel')}
        loading={isSaving}
        testId="homeChannelConfirmationModal"
      >
        {saveError && <P className={styles.saveError}>{saveError}</P>}
      </ConfirmationModal>
    </>
  );
}
